/**
 *
 * DomesticSolutionCard
 *
 * A specialized card for DHL Supply Chain Australia domestic pallet transportation
 */

import React from "react";
import PropTypes from "prop-types";
import {
  IntlPropType,
  trackConversionInteraction,
} from "root/libs/core-libs/src";
import { CountrySettings } from "root/libs/ui-containers/src";
import { Sizes } from "@dhl-official/stencil-library";
import { Icon, Link } from "root/libs/ui-components/src";

import { selectTrackingKey } from "one-time-shipment/src/containers/Tracking/selectors";
import OfferPriceAndPaymentInfo from "one-time-shipment/src/components/Offers/OfferPriceAndPaymentInfo";
import OfferAddOns from "one-time-shipment/src/components/Offers/OfferAddOns";
import OfferProperties from "one-time-shipment/src/components/Offers/OfferProperties";
import FrequentShipmentIcon from "iconsTwoColors/cx/DHL_RoadFreight_02.svg";

import {
  DomesticSolutionCardContent,
  DomesticSolutionCardWrapper,
  IntroContent,
  CreateAccountWrapper,
  CreateAccountContent,
  OfferCardDetailsContent,
  StyledAccordion,
  IntroInfoWrapper,
  IntroContentWrapper,
  RegularShipperText,
  CreateAccountHeadline,
  PromotedTextWrapper,
  PromotedText,
  CardButtonWrapper,
} from "./styled";

const { withCountry } = CountrySettings;

const COPY_ID_PREFIX = "DomesticSolutionCard";

export default class DomesticSolutionCard extends React.PureComponent {
  static propTypes = {
    intl: IntlPropType.isRequired,
    contentKey: PropTypes.string,
    utfDataObject: PropTypes.object,
    className: PropTypes.string,
    reference: PropTypes.object,
    trackEvent: PropTypes.func,
    index: PropTypes.number,
    indexLength: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.OfferPriceAndPaymentInfoWithCountry = withCountry(
      OfferPriceAndPaymentInfo
    );
    this.OfferProperties = withCountry(OfferProperties);
    this.OfferAddOnsWithCountry = withCountry(OfferAddOns);
    this.trackingKeySelector = selectTrackingKey;
    this.idExploredOfferTrakingKeyPath = this.trackingKeySelector(
      "spotShipment.interactions.offer.idExploredOffer"
    );
  }

  trackUtfEvent = (utfDataObject) => {
    trackConversionInteraction(utfDataObject);
  };

  onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
    }
  };

  getCopy = (id) => {
    const { intl, contentKey } = this.props;
    return intl.formatMessage({ id: `${COPY_ID_PREFIX}.${contentKey}_${id}` });
  };

  render() {
    const {
      className,
      intl,
      reference,
      trackEvent,
      index,
      indexLength,
      contentKey,
      utfDataObject,
    } = this.props;

    const accordionId = `offerCard-dsc-product`;

    const cardProperties = [
      {
        key: "AU_WIDE_COVERAGE",
        promoted: false,
      },
      {
        key: "SECURITY",
        promoted: false,
      },
      {
        key: "COLLECT_DATA",
        promoted: false,
      },
      {
        key: "SUSTATINABILITY",
        promoted: false,
      },
    ];

    const cardAddOns = [
      {
        key: "TRANSPORT_NETWORKS",
      },
      {
        key: "DEDICATED_TRANSPORT",
      },
      {
        key: "SPECIALIST",
      },
      {
        key: "CONNNECTED_CONTROL",
      },
    ];

    const ariaDescribedByIdCollection = `${contentKey}-firstline ${contentKey}-headline ${contentKey}-promoted ${contentKey}-moreInfo`;
    const offerIndex = index + 1;

    const messages = {
      firstline: this.getCopy("firstline"),
      headline: this.getCopy("headline"),
      promoted: this.getCopy("promoted"),
      linkText: this.getCopy("linkText"),
      linkUrl: this.getCopy("linkUrl"),
      saveQuote: this.getCopy("saveQuote"),
    };

    return (
      <DomesticSolutionCardWrapper className={className}>
        <DomesticSolutionCardContent
          data-testid="domestic-solution-card"
          data-testvalue={contentKey}
          aria-describedby={ariaDescribedByIdCollection}
          aria-label={intl.formatMessage(
            { id: `OfferCardHorizontal.screenReaderLabel_offersAmount` },
            {
              currentOfferIndex: offerIndex,
              totalOffersAmount: indexLength,
            }
          )}
          tabIndex={0}
        >
          <IntroContent>
            <IntroInfoWrapper>
              <Icon
                icon={FrequentShipmentIcon}
                width={{
                  mobile: "77px",
                  desktop: "72px",
                }}
                height={{
                  mobile: "72px",
                  desktop: "52px",
                }}
                useAs="decorative"
                alt="truck-image"
              />
              <IntroContentWrapper>
                <RegularShipperText
                  dataTestid="domestic-solution-card-firstline"
                  size={Sizes.XS}
                  id={`${contentKey}-firstline`}
                  isParagraph
                >
                  {messages.firstline}
                </RegularShipperText>
                <CreateAccountWrapper id={`${contentKey}-headline`}>
                  <CreateAccountHeadline
                    dataTestid="domestic-solution-card-headline"
                    weight={800}
                    inlined
                    priority={4}
                    className="horizontal-card-headline"
                  >
                    {messages.headline}
                  </CreateAccountHeadline>
                </CreateAccountWrapper>
                <PromotedTextWrapper>
                  <PromotedText
                    dataTestid="domestic-solution-card-promoted"
                    size={Sizes.XS}
                    id={`${contentKey}-promoted`}
                    isParagraph
                  >
                    {messages.promoted}
                  </PromotedText>
                </PromotedTextWrapper>
              </IntroContentWrapper>
            </IntroInfoWrapper>
          </IntroContent>
          <CreateAccountContent>
            <CardButtonWrapper>
              <Link
                href={messages.linkUrl}
                dataTestid="domestic-solution-card-cta"
                linkType="cta primary"
                target="_blank"
                disableMinWidth
                onClick={() => this.trackUtfEvent(utfDataObject)}
              >
                {messages.linkText}
              </Link>
            </CardButtonWrapper>
          </CreateAccountContent>
        </DomesticSolutionCardContent>
        <span id={`${contentKey}-moreInfo`} className="visually-hidden">
          {intl.formatMessage({
            id: `OfferCardHorizontal.screenReaderLabel_moreInformation`,
          })}
        </span>
        <StyledAccordion
          expandCollapseButtonTestId={`expand-collapse-${contentKey}`}
          expandButtonLabel={intl.formatMessage({
            id: `OfferCardHorizontal.exploreOffer`,
          })}
          collapseButtonLabel={intl.formatMessage({
            id: `OfferCardHorizontal.closeOffer`,
          })}
          expandButtonAriaLabel={intl.formatMessage({
            id: `OfferCardHorizontal.screenReaderLabel_ariaExploreOffer`,
          })}
          collapseButtonAriaLabel={intl.formatMessage({
            id: `OfferCardHorizontal.screenReaderLabel_ariaCloseOffer`,
          })}
          expandButtonAriaControls={`${accordionId}-content`}
          expandButtonDataTracking={this.idExploredOfferTrakingKeyPath}
          onKeyDown={this.onKeyDown}
        >
          <StyledAccordion.Trigger
            id={`${accordionId}-title`}
            ariaControls={`${accordionId}-content`}
            ref={reference}
          />
          <StyledAccordion.Content
            id={`${accordionId}-content`}
            dataTestid="domestic-solution-card-accordion-content"
            labeledBy={`${accordionId}-title`}
            className="offerCollapsibleContent"
          >
            <OfferCardDetailsContent>
              <div>
                <this.OfferProperties
                  customHeadline={`${COPY_ID_PREFIX}.PROPERTIES_headline`}
                  properties={cardProperties}
                  offerId={contentKey}
                  intl={intl}
                  iNeedTheseProps={["is12HourTimeFormat"]}
                />
              </div>
              <this.OfferAddOnsWithCountry
                trackEvent={trackEvent}
                addOns={cardAddOns}
                offerKey={contentKey}
                intl={intl}
                showCurrencyIsoCodeForPrices={false}
                iNeedTheseProps={[
                  "nativeLanguageCode",
                  "formatCurrencyWithNativeLocale",
                ]}
                customHeadline={`${COPY_ID_PREFIX}.ADDONS_headline`}
              />
            </OfferCardDetailsContent>
          </StyledAccordion.Content>
        </StyledAccordion>
      </DomesticSolutionCardWrapper>
    );
  }
}
